import {useState} from "react";
import {Stack, Box, Paper, CircularProgress, Typography} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useBooking from "../hooks/useBooking";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import BookingTypesDropdownComponent from "../common/booking-types-dropdown.component";
import CustomDatePicker from "../../../core/components/common/custom-datepicker.component";
import dayjs from "dayjs";
import DateRangePickerComponent from "../../../core/components/common/custom-date-range-picker.component";
import moment from "moment";


const CreateBookingComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {isCreatingBooking, createBooking} = useBooking();

    const [purposeInput, setPurposeInput] = useState("");
    const [selectedBookingType, setSelectedBookingType] = useState(null);
    const [customScheduleIn, setCustomScheduleIn] = useState(moment().format("YYYY-MM-DD"));
    const [customScheduleOut, setCustomScheduleOut] = useState(moment().add(1, "day").format("YYYY-MM-DD"));
    const [scheduleIn, setScheduleIn] = useState(dayjs(customScheduleIn));
    const [scheduleOut, setScheduleOut] = useState(dayjs(customScheduleOut));


    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    const handleBookingTypeChange = (value) => {
        setSelectedBookingType(value);
    }

    const handleTextfieldChange = (e) => {
        setPurposeInput(e.target.value);
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/bookings");
        }
    }

    const handleCreateBooking = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const payload = {
                booking_type: selectedBookingType,
                purpose: purposeInput,
                valid_from: dayjs(new Date(scheduleIn)).format("YYYY-MM-DD"),
                valid_to: dayjs(new Date(scheduleOut)).format("YYYY-MM-DD"),
                org_uid: organizationId
            }

            createBooking(payload, orgIdFromUrlForPortEmployee);

        } else {
            const payload = {
                booking_type: selectedBookingType,
                purpose: purposeInput,
                valid_from: dayjs(new Date(scheduleIn)).format("YYYY-MM-DD"),
                valid_to: dayjs(new Date(scheduleOut)).format("YYYY-MM-DD")
            }

            createBooking(payload);
        }
    }
    

    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Booking List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Create New Booking Request"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Add new booking request for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Booking Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to create a new booking request"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Booking Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />

                                <BookingTypesDropdownComponent 
                                    value={selectedBookingType}
                                    onChange={handleBookingTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isCreatingBooking}
                                />
                                 {!selectedBookingType && (<Typography sx={{fontSize: 11}} variant="caption" color="red">Select booking type is required</Typography>)}
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Valid From"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <Box>
                                    <CustomDatePicker
                                        label="Valid From"
                                        value={scheduleIn}
                                        onChange={(newDate) => setScheduleIn(newDate)}
                                        disabled={isCreatingBooking}
                                    />
                                </Box>
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Purpose"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter purpose"}
                                    variant={"outlined"}
                                    value={purposeInput}
                                    onChange={(e) => handleTextfieldChange(e)}
                                    fullWidth={true}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isCreatingBooking}
                                    helperText={!purposeInput ? "Container number is required" : false}
                                />           
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Valid To"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <Box>
                                    <CustomDatePicker
                                        label="Valid To"
                                        value={scheduleOut}
                                        onChange={(newDate) => setScheduleOut(newDate)}
                                        disabled={isCreatingBooking}
                                        minDate={scheduleIn}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Stack>   

                    <Stack direction={{xs: "column", md: "row"}} spacing={4} sx={{mt: 3}}>
                        <DateRangePickerComponent
                            initialStartDate={moment().format("YYYY-MM-DD")}
                            initialEndDate={moment().add(1, "day").format("YYYY-MM-DD")}
                            disabled={!!scheduleIn || !!scheduleOut}
                            ranges={{
                                "For a Day": [moment(), moment().add(1, "day")],
                                "For a Week": [moment(), moment().add(6, "days")],
                                "For a Month": [moment(), moment().add(1, "month").subtract(1, "day")],
                            }}
                            onDateRangeChange={(start, end) => {
                                const formattedStart = moment(start).format('YYYY-MM-DD');
                                const formattedEnd = moment(end).format('YYYY-MM-DD');

                                setCustomScheduleIn(formattedStart);
                                setCustomScheduleOut(formattedEnd);
                                setScheduleIn(dayjs(start));
                                setScheduleOut(dayjs(end));
                            }}
                        />                   
                    </Stack>

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isCreatingBooking) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Submit Request"}
                            startIcon={isCreatingBooking && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (purposeInput && selectedBookingType && scheduleIn && scheduleOut && !isCreatingBooking) ? "100%" : "55%",
                                cursor: (purposeInput && selectedBookingType && scheduleIn && scheduleOut && !isCreatingBooking) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={
                                (purposeInput && selectedBookingType && scheduleIn && scheduleOut && !isCreatingBooking) ? handleCreateBooking : null
                            }
                        />    
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default CreateBookingComponent